import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  goToShippingMethods,
  setCountry,
  setEmail,
  setBillingAddressFromCart,
  setShippingAddressFromCart,
  setShippingAddressFromUser,
} from '../Slices/ShippingSlice.jsx';
import CommerceApiFetchWrapper from '/Foundation/Common/code/Utils/CommerceApiFetchWrapper';
import { isLoggedIn } from '../Helpers/UiHelper.js';
import { loadShippingMethods } from './ShippingThunks.jsx';
import { removeProduct } from '/Foundation/Common/code/Helpers/ProductQuantityHelper';
import { setCartIdCookie } from '/Foundation/React/code/Utilities/HelperUtilities';
import { addMessage, removeMessage } from '../Slices/MessageSlice.jsx';
import { setUnavailableProducts } from '../Slices/CartSlice.jsx';
import { getAnalyticsDataForCartProducts } from '/Foundation/Common/code/Helpers/TrackingHelper.js';
import { setEstimatedShippingOnCart } from '/Foundation/Common/code/Helpers/ShippingHelper.js';

export const getCart = createAsyncThunk('getCart', async (_, { dispatch, rejectWithValue }) => {
  let isB2CEnabled = _THULEDATA?.isB2CEnabled;
  if (!isB2CEnabled) return rejectWithValue('getCart failed due to B2C not being enabled for this market');

  let cart = await CommerceApiFetchWrapper('/cart/get-cart?refresh=true', { method: 'GET' });
  if (!cart) {
    setCartIdCookie(null);
    return rejectWithValue('getCart - no cart found.');
  }

  if (cart.shippingAddress && Object.keys(cart.shippingAddress).length !== 0) {
    dispatch(setShippingAddressFromCart(cart));
  }
  if (cart.billingAddress && Object.keys(cart.billingAddress).length !== 0) {
    dispatch(setBillingAddressFromCart(cart));
  }
  if (!(cart.shippingAddress && Object.keys(cart.shippingAddress).length !== 0)) {
    if (isLoggedIn()) {
      var userAddress = await CommerceApiFetchWrapper('/customer/shipping-address', { method: 'GET' });
      if (userAddress) {
        dispatch(setShippingAddressFromUser(userAddress));
      } else {
        dispatch(setEmail(window._THULEDATA._Checkout?.email));
        dispatch(setCountry(cart.countryName));
      }
    } else {
      dispatch(setCountry(cart.countryName));
    }
  }

  if (cart?.lineItems?.length > 0 && !cart.shippingInfo && window._THULEDATA._Cart?.showDelivery) {
    cart = await setEstimatedShippingOnCart();
  }

  // Get analytics data for all tracking + Update Adobe Data Layer with cart products
  await getAnalyticsDataForCartProducts(cart);

  return cart;
});

export const applyDiscount = createAsyncThunk(
  'applyDiscount',
  async (code, { getState, rejectWithValue, dispatch }) => {
    const cartState = getState().Cart;

    if (cartState.discountCode) {
      dispatch(removeDiscount());
    }

    let cart = await CommerceApiFetchWrapper('/cart/discount-code', {
      method: 'POST',
      body: JSON.stringify({
        Code: code,
      }),
    });

    if (!cart || !cart.discountCode) return rejectWithValue(cart);

    dispatch(reloadShippingMethods());

    return cart;
  }
);

export const removeDiscount = createAsyncThunk('removeDiscount', async (_, { getState, rejectWithValue, dispatch }) => {
  let cartState = getState().Cart;
  let cart = await CommerceApiFetchWrapper('/cart/remove-discount-code', {
    method: 'POST',
    body: JSON.stringify({
      TypeId: cartState.discountCode.typeId,
      Id: cartState.discountCode.id,
    }),
  });

  if (!cart) {
    dispatch(
      addMessage({
        id: new Date().valueOf() * Math.random(),
        content: window._THULEDATA._Cart.generalErrorMessage,
        type: 'error',
      })
    );
    return rejectWithValue('removeDiscount failed');
  }

  dispatch(reloadShippingMethods());

  return cart;
});

export const removeCartline = createAsyncThunk(
  'removeCartline',
  async (payload, { getState, dispatch, rejectWithValue }) => {
    const result = await dispatch(
      removeProduct({ externalId: payload.id, adobeAnalyticsData: payload.adobeAnalyticsData })
    );
    if (!result?.payload) {
      dispatch(
        addMessage({
          id: new Date().valueOf() * Math.random(),
          content: window._THULEDATA._Cart.generalErrorMessage,
          type: 'error',
        })
      );
      return rejectWithValue('removeCartline failed');
    }

    let state = getState();
    const unavailableProducts = state.Cart.unavailableProducts;
    if (unavailableProducts?.length > 0) {
      let newUnavailableProducts = [...unavailableProducts];

      newUnavailableProducts.forEach((product) => {
        if (!result.payload.lineItems.find((x) => x.id === product)) {
          newUnavailableProducts.splice(product, 1);
        }
      });

      state.Messages.messages?.forEach((message) => {
        if (message.id.includes('unavailableProducts_')) {
          let lineId = message.id.split('_')[1];
          if (!result.payload.lineItems.find((x) => x.id === lineId)) {
            dispatch(removeMessage(message.id));
          }
        }
      });

      dispatch(setUnavailableProducts(newUnavailableProducts));
    }

    dispatch(reloadShippingMethods());
  }
);

export const finalizeDiscountCodes = createAsyncThunk('finalizeDiscountCodes', async () => {
  // Make sure discount codes and tax are correct since commercetools can remove discount codes
  return await CommerceApiFetchWrapper('/cart/finalize-discount-codes', {
    method: 'POST',
  });
});

export const reloadShippingMethods = createAsyncThunk('reloadShippingMethods', async (_, { getState, dispatch }) => {
  let state = getState();
  //When changes are made to the cart we need to fetch shipping methods again to show correct prices in the shipping method list
  if (
    (state.Shipping.shippingSteps.isShippingMethods || state.Shipping.shippingSteps.isPayment) &&
    state.Cart.lineItems?.length > 0
  ) {
    dispatch(loadShippingMethods());
    //Making changes to the cart could change the available shipping methods
    //If the user has passed the shipping method step when making changes to the products,
    //we should go back to avoid strange behavior with the user's selected shipping method disappearing etc.
    if (state.Shipping.shippingSteps.isPayment) dispatch(goToShippingMethods());
  }
});
